/**
 * Translation Service
 * Provides multi-language translation using free APIs with caching
 */

const SUPPORTED_LANGUAGES = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  pt: 'Portuguese',
  it: 'Italian',
  zh: 'Chinese (Simplified)',
  ja: 'Japanese',
  ko: 'Korean',
  ru: 'Russian',
  ar: 'Arabic',
  hi: 'Hindi',
  nl: 'Dutch',
  pl: 'Polish',
  tr: 'Turkish',
};

// Cache for translations to avoid repeated API calls
const translationCache = new Map();

/**
 * Translate text using libre-translate API (free, no key required)
 * @param {string} text - Text to translate
 * @param {string} targetLang - Target language code (e.g., 'es', 'fr')
 * @param {string} sourceLang - Source language code (default: 'auto')
 * @returns {Promise<string>} Translated text
 */
export async function translateText(text, targetLang = 'es', sourceLang = 'auto') {
  if (!text || !text.trim()) return text;

  // Check cache first
  const cacheKey = `${text}|${sourceLang}|${targetLang}`;
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }

  try {
    const params = new URLSearchParams({
      q: text,
      langpair: `${sourceLang === 'auto' ? 'en' : sourceLang}|${targetLang}`,
    });

    const result = await fetch(
      `https://api.mymemory.translated.net/get?${params}`,
      { headers: { 'Accept': 'application/json' } }
    ).then(r => r.json());

    const translated = result?.responseData?.translatedText || text;
    
    // Cache the result
    translationCache.set(cacheKey, translated);
    return translated;
  } catch (error) {
    console.warn('Translation failed, returning original text:', error);
    return text;
  }
}

/**
 * Translate all text content in a DOM element recursively
 * @param {HTMLElement} element - DOM element to translate
 * @param {string} targetLang - Target language code
 * @param {string} sourceLang - Source language code
 * @returns {Promise<void>}
 */
export async function translateElement(element, targetLang = 'es', sourceLang = 'auto') {
  if (!element) return;

  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );

  const nodesToTranslate = [];
  let node;

  while ((node = walker.nextNode())) {
    const text = node.textContent.trim();
    if (text && text.length > 0 && !/^\d+$/.test(text)) {
      nodesToTranslate.push(node);
    }
  }

  // Translate each text node
  for (const node of nodesToTranslate) {
    const translated = await translateText(node.textContent, targetLang, sourceLang);
    node.textContent = translated;
  }
}

/**
 * Get list of supported languages
 * @returns {Object} Language codes and names
 */
export function getSupportedLanguages() {
  return SUPPORTED_LANGUAGES;
}

/**
 * Clear translation cache
 */
export function clearTranslationCache() {
  translationCache.clear();
}

/**
 * Get language name by code
 * @param {string} code - Language code
 * @returns {string} Language name
 */
export function getLanguageName(code) {
  return SUPPORTED_LANGUAGES[code] || code;
}